<script setup lang="ts">
import type { BrandAppShowcaseStore } from '../lib/brandAppContract'
import type { ImageFile } from '@/types/types'
import type { PanelTab } from '../lib/brandAppSections'
import HomePanel from './sections/HomePanel.vue'
import NavigationPanel from './sections/NavigationPanel.vue'
import LoyaltyPanel from './sections/LoyaltyPanel.vue'
import LoyaltyLayoutPanel from './sections/LoyaltyLayoutPanel.vue'
import OrderPanel from './sections/OrderPanel.vue'
import StoresPanel from './sections/StoresPanel.vue'
import StoreActionsPanel from './sections/StoreActionsPanel.vue'
import HeroBlockConfig from './sections/home/HeroBlockConfig.vue'
import OffersBlockConfig from './sections/home/OffersBlockConfig.vue'
import NewsBlockConfig from './sections/home/NewsBlockConfig.vue'
import MenuBlockConfig from './sections/home/MenuBlockConfig.vue'
import SocialBlockConfig from './sections/home/SocialBlockConfig.vue'
import PanelGroup from './PanelGroup.vue'
import ImageUploaderNew from '@/components/ui/ImageUploader/ImageUploaderNew.vue'
import RegistrationStepPanel from './sections/loyalty/RegistrationStepPanel.vue'
import MissionsPanel from './sections/loyalty/MissionsPanel.vue'
import AudienceRulesPanel from './sections/loyalty/AudienceRulesPanel.vue'
import RecoveryPanel from './sections/loyalty/RecoveryPanel.vue'
import OrderChannelFields from './sections/order/OrderChannelFields.vue'
import { REGISTRATION_STEP_SECTIONS, orderChannelForSection } from '../lib/brandAppSections'
import type { RegistrationStepId } from '../lib/brandAppRegistration'

const props = defineProps<{
  section: string
  loyaltyStores: BrandAppShowcaseStore[]
  images: ImageFile[]
  initialImages: ImageFile[]
  deletedImages: number[]
  tab?: PanelTab
  highlightId?: string | null
}>()

const emit = defineEmits<{
  (e: 'update:images', value: string[]): void
  (e: 'update:deleted-images', value: number[]): void
}>()

const { t } = useI18n()

const registrationStep = computed(() =>
  props.section in REGISTRATION_STEP_SECTIONS ? (props.section as RegistrationStepId) : null
)

const orderChannel = computed(() => orderChannelForSection(props.section))
</script>

<template>
  <div class="flex flex-col">
    <HeroBlockConfig v-if="section === 'hero'" />
    <OffersBlockConfig v-else-if="section === 'offers'" :tab="tab" />
    <NewsBlockConfig v-else-if="section === 'news'" :tab="tab" />
    <MenuBlockConfig v-else-if="section === 'menu'" :tab="tab" :stores="loyaltyStores" />
    <SocialBlockConfig v-else-if="section === 'social'" :tab="tab" />
    <LoyaltyPanel
      v-else-if="section === 'loyalty'"
      :images="images"
      :initial-images="initialImages"
      :deleted-images="deletedImages"
      :tab="tab"
      @update:images="emit('update:images', $event)"
      @update:deleted-images="emit('update:deleted-images', $event)"
    />
    <PanelGroup v-else-if="section === 'loyalty-images'">
      <ImageUploaderNew
        :images="images"
        :initial-images="initialImages"
        :deleted-images="deletedImages"
        :aspect-ratio="[9, 16]"
        :ratio-name="t('pages.loyaltySupports.webapp.editor.loyalty.storyFormat')"
        @update:images="emit('update:images', $event)"
        @update:deleted-images="emit('update:deleted-images', $event)"
      />
    </PanelGroup>
    <RegistrationStepPanel v-else-if="registrationStep" :step="registrationStep" :tab="tab" />
    <MissionsPanel v-else-if="section === 'missions'" />
    <AudienceRulesPanel v-else-if="section === 'audience-rules'" />
    <PanelGroup v-else-if="section === 'recovery'">
      <RecoveryPanel />
    </PanelGroup>
    <PanelGroup v-else-if="orderChannel">
      <OrderChannelFields :channel="orderChannel" :stores="loyaltyStores" />
    </PanelGroup>
    <OrderPanel v-else-if="section === 'order'" :stores="loyaltyStores" />
    <template v-else-if="section === 'stores'">
      <StoreActionsPanel :stores="loyaltyStores" />
      <StoresPanel :stores="loyaltyStores" />
    </template>
    <StoreActionsPanel v-else-if="section === 'stores-actions'" :stores="loyaltyStores" />
    <StoresPanel v-else-if="section === 'stores-list'" :stores="loyaltyStores" />
    <NavigationPanel v-else-if="section === 'navigation'" />
    <HomePanel v-else-if="section === 'home'" :highlight-id="highlightId" />
    <LoyaltyLayoutPanel v-else-if="section === 'loyalty-layout'" />
  </div>
</template>
